/**
 * Self-heal for the known, non-deterministic @firebase/firestore bug
 * "FIRESTORE (x) INTERNAL ASSERTION FAILED: Unexpected state" (Sentry
 * BAR-HELPER-2Q5/2FY/2KZ). When it occurs the Firestore client is corrupt and
 * ALL further operations fail — the app is effectively dead until a manual
 * reload. We cannot fix the SDK, but we can recover the user's session:
 * detect the assertion and reload the page (which re-initializes the client).
 *
 * Loop guard: only one recovery attempt per RECOVERY_WINDOW_MS, so an assertion
 * that recurs immediately after reload does not cause a reload storm.
 */

const FATAL_PATTERN = /FIRESTORE.*INTERNAL ASSERTION FAILED/i;
const RECOVERY_KEY = 'bh.firestoreRecoveryAt';
const RECOVERY_WINDOW_MS = 60_000;

type SimpleStorage = Pick<Storage, 'getItem' | 'setItem'>;

function extractMessage(error: unknown, depth = 0): string {
  if (!error || depth > 4) return '';
  if (typeof error === 'string') return error;
  const e = error as { message?: unknown; originalError?: unknown };
  const parts: string[] = [];
  if (typeof e.message === 'string') parts.push(e.message);
  if (e.originalError) parts.push(extractMessage(e.originalError, depth + 1));
  return parts.join(' ');
}

/** Is the error the fatal Firestore assertion (corrupt client)? */
export function isFatalFirestoreError(error: unknown): boolean {
  return FATAL_PATTERN.test(extractMessage(error));
}

/**
 * Should we attempt recovery now? True at most once per RECOVERY_WINDOW_MS
 * (writes the timestamp on true). Takes `now` and `storage` as arguments so
 * the logic is pure and unit-testable.
 */
export function shouldAttemptRecovery(now: number, storage: SimpleStorage): boolean {
  const last = Number(storage.getItem(RECOVERY_KEY) ?? '0');
  if (Number.isFinite(last) && now - last < RECOVERY_WINDOW_MS) {
    return false;
  }
  storage.setItem(RECOVERY_KEY, String(now));
  return true;
}
